import { consumeChallenge, issueChallenge, pendingChallenges } from "../challenges";
import { MODE_STRONG } from "../constants";
import { decodePresentation, encodeChallengeQr } from "../format";
import { emitQR } from "../qr";
import { receiptDraft, recordReceipt } from "../receipts";
import { startScanner } from "../scan";
import { checkStatus } from "../status";
import {
  correctedNow, loadStatusList, loadTrustStore, recentNonces, rememberNonce,
} from "../storage";
import { currentClockAlarms } from "../sync";
import { alarmsHTML, escapeHTML, REASON_COPY } from "../ui";
import { verifyPresentation } from "../verify";
import { renderResult, type ResultView } from "./result";
import { ICON } from "../icons";

/**
 * The camera, as a parameter, so a test can drive this screen without one.
 *
 * Nothing else here is replaced: the verification, the trust store, the clock
 * and the nonce memory are all the real modules. A camera is the one input a
 * test runner cannot have, and the replay this screen has to refuse lives in
 * what happens after the payload arrives, not in how it arrived.
 */
export type Scanner = typeof startScanner;

export async function renderScan(
  root: HTMLElement,
  scanner: Scanner = startScanner,
): Promise<() => void> {
  // `stop` is a `let` the handler reaches through `stop?.()`. Written as a
  // `const` assigned from the await below, the callback would close over a
  // binding still in its temporal dead zone, and a payload delivered before
  // `scanner` resolves would throw a ReferenceError instead of verifying.
  let stop: (() => void) | null = null;
  let stopped = false;
  // Which camera start is the current one; see `showCamera`.
  let cameras = 0;

  const onPayload = (payload: Uint8Array) => {
    if (stopped) return;
    stopped = true;
    void (async () => {
      stop?.();
      // Everything between here and `renderResult` is storage, and storage on
      // a phone fails: `correctedNow`, `loadTrustStore` and `recentNonces`
      // reject when `indexedDB.open` errors, and `rememberNonce` rejects on
      // `onerror` and on `onabort` too — a device at its quota, or a user
      // clearing site data mid-scan. `verifyPresentation` itself never throws. Uncaught, any of
      // those leaves a verifier looking at a stopped camera with no result and
      // no error, and the rejection in a console nobody reads. A verifier must
      // always be told something.
      let result: ResultView;
      try {
        const { now, synced } = await correctedNow();
        // Read once, before verifying, so the check inside is a lookup.
        const statusList = await loadStatusList();
        result = await verifyPresentation(payload, {
          issuerPublicKeys: await loadTrustStore(),
          now,
          clockSynced: synced,
          // `recentNonces` builds a fresh Set on every call and
          // `verifyPresentation` adds to whatever Set it is handed, so the
          // verifier's own write lands in a snapshot that is thrown away the
          // moment this function returns. The two APIs look like they compose
          // and they do not. `rememberNonce` below is the only durable write,
          // and without it this screen has no replay detection at all: a
          // photograph of somebody's QR is accepted every time it is scanned.
          seenNonces: await recentNonces(),
          // The same snapshot shape: `verifyPresentation` deletes the answered
          // challenge from a Set that dies here, and `consumeChallenge` below
          // is the durable half.
          pendingChallenges: await pendingChallenges(),
          status: (index) => checkStatus(statusList, index),
        });
        if (result.ok) {
          // A second decode, because a `VerifyResult` carries no nonce and
          // teaching it to carry one would be a change to the verification
          // layer for the benefit of a screen. `verifyPresentation` decoded
          // these same bytes to reach `ok` and `decodePresentation` is a pure
          // function of them, so this decode is not what can fail here — the
          // write is.
          const { nonce } = decodePresentation(payload);
          if (result.mode !== MODE_STRONG) {
            await rememberNonce(nonce);
          } else if (!(await consumeChallenge(nonce))) {
            // Spent between the snapshot above and now: a second scan of the
            // same answer that finished first, or the two minutes ran out.
            // Either way this scan is the replay, and it gets the replay's
            // refusal.
            result = { ok: false, reason: "challenge-unknown" };
          }
        }
      } catch {
        // A refusal rather than the `ok` we may already be holding: a nonce
        // this device failed to write down is a code it cannot refuse the
        // second time, so accepting it here would turn one storage error into
        // a replay that works.
        result = { ok: false, reason: "device-error" };
      }
      renderResult(root, result, () => void showCamera());
      // After the result is on screen and never in its way: the verifier has
      // their answer, and a receipt that cannot be written costs a line of
      // history, not a verification. The reason recorded is the one shown.
      const draft = receiptDraft(payload, result.reason);
      if (draft) void recordReceipt(draft).catch(() => {});
    })();
  };

  /**
   * One screen, restarted in place, rather than a fresh `renderScan` per scan.
   * The teardown handed back below closes over `stop`, so a second screen
   * started behind it would leave the caller holding a function that turns off
   * a camera nobody is looking through and leaves the live one running.
   */
  const showCamera = async () => {
    root.innerHTML = `<div id="alarms"></div>
                      <div class="viewfinder"><video id="camera" playsinline muted></video></div>
                      <p>Apunta al código de la otra persona</p>
                      <div class="foot"><button id="strong" class="ghost">${ICON.shield}
                      Modo reforzado</button></div>`;
    root.querySelector("#strong")!.addEventListener("click", () => void showChallenge());
    // Not awaited ahead of the camera: a verifier should not wait on storage
    // to start scanning, and the warning is advice, not a gate.
    const alarms = root.querySelector<HTMLDivElement>("#alarms")!;
    void currentClockAlarms().then(
      (found) => void (alarms.innerHTML = alarmsHTML(found)),
      () => {},
    );
    const video = root.querySelector<HTMLVideoElement>("#camera")!;
    stopped = false;
    // «Modo reforzado» can take the verifier off this camera while it is
    // still starting. The `<video>` it was starting on is then gone, `play()`
    // rejects, and without this the camera problem would be drawn over the
    // challenge; a start that resolves late would keep a second camera running
    // behind the next one. A start that belongs to a camera the screen has
    // since left is stopped or ignored, and its payloads go nowhere.
    const mine = ++cameras;
    let started: () => void;
    try {
      started = await scanner(video, (payload) => {
        if (mine === cameras) onPayload(payload);
      });
    } catch {
      if (mine !== cameras || stopped) return;
      // The camera prompt was refused or dismissed, or there is no
      // `navigator.mediaDevices` at all because the page is on plain HTTP.
      // Uncaught, the `<video>` and `Apunta al código` above are already on
      // screen and stay there: a black rectangle and an instruction, for as
      // long as the verifier is willing to hold the phone up.
      showCameraProblem();
      return;
    }
    // A payload can arrive before `scanner` resolves. If it did, the camera is
    // still running and nothing has turned it off yet.
    if (mine !== cameras || stopped) started();
    else stop = started;
  };

  /**
   * Strong mode, the verifier's first half: a challenge this phone remembers
   * for two minutes, shown as a QR for the holder to answer. The camera stays
   * off while it is on screen; the answer is scanned by the same camera and
   * the same verification as any other code, which is also why a fast-mode
   * code scanned after this still verifies.
   */
  async function showChallenge(): Promise<void> {
    stopped = true;
    stop?.();
    let qr: string;
    try {
      qr = (await emitQR(encodeChallengeQr(await issueChallenge())))[0];
    } catch {
      renderResult(root, { ok: false, reason: "device-error" }, () => void showCamera());
      return;
    }
    root.innerHTML = `<div class="problem qr-page"><h1>Modo reforzado</h1>
      <p>Pide a la otra persona que toque «Responder a un desafío» en su identidad y
      escanee este código. Vale dos minutos y sirve para una sola respuesta.</p>
      <div class="qr" id="challenge-qr">${qr}</div>
      <div class="foot"><button id="scan-answer">Escanear la respuesta</button></div></div>`;
    root
      .querySelector<HTMLButtonElement>("#scan-answer")!
      .addEventListener("click", () => void showCamera());
  }

  /** The same shape as a refusal — a sentence and a way to try again — because
   *  from the corridor it is the same thing: the scan did not happen. */
  function showCameraProblem(): void {
    root.innerHTML = `<div class="problem"><div class="badge">${ICON.cross}</div>
      <h1>Sin cámara</h1><p>${escapeHTML(REASON_COPY["camera-unavailable"])}</p>
      <div class="foot"><button id="retry">Reintentar</button></div></div>`;
    root
      .querySelector<HTMLButtonElement>("#retry")!
      .addEventListener("click", () => void showCamera());
  }

  await showCamera();

  return () => {
    stopped = true;
    stop?.();
  };
}
